import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { selectionToLeafObjectIds } from "../app/objectSelection.ts";
import { saveSceneToCloudLibrary } from "../app/saveToLibrary.ts";
import type { CommandBuffer } from "../app/store.ts";
import { useStore } from "../app/store.ts";
import { getCommandMetadata } from "../modeling/operatorParams.ts";
import { topologyFaceCount } from "../modeling/topology.ts";
import { Button, IconButton } from "./Button.tsx";
import { CubeSpinner } from "./CubeSpinner.tsx";
import { SaveIcon } from "./Icons.tsx";
import { ModalShell } from "./ModalShell.tsx";
import { PANEL_INPUT_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_CAUTION,
  COLOR_TEXT,
  COLOR_TEXT_DISABLED,
  FONT_SIZE_MODAL_TITLE,
  FONT_SIZE_TINY,
  RADIUS_SM,
  useChromeDim,
  Z_TOOLBAR,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Keyframe styles (injected once)
// ---------------------------------------------------------------------------

let stylesInjected = false;
function ensureKeyframes() {
  if (stylesInjected) return;
  stylesInjected = true;
  const sheet = document.createElement("style");
  sheet.textContent = `
@keyframes blink { 50% { opacity: 0; } }`;
  document.head.appendChild(sheet);
}

// ---------------------------------------------------------------------------
// DirtyIndicator — compact save button for the navbar
// ---------------------------------------------------------------------------

/** Compact save button that smoothly expands/collapses without shifting the header. */
export function DirtyIndicator() {
  ensureKeyframes();

  const dirty = useStore((s) => s.sceneDirty);
  const saving = useStore((s) => s.sceneSaving);

  // `shown` tracks the CSS-visible state; it lags behind `dirty` so the
  // collapse transition can finish before we hide the button.
  const [shown, setShown] = useState(dirty);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (dirty) {
      setShown(true);
    } else if (wasSaving.current) {
      // After a save, collapse immediately (spinner already gave feedback)
      setShown(false);
    }
    // When dirty goes false without a save (e.g. undo back to clean),
    // the CSS transition handles the smooth collapse, then onTransitionEnd
    // will set shown=false.
  }, [dirty]);

  useEffect(() => {
    wasSaving.current = saving;
  }, [saving]);

  // Determine if the button should be expanded (taking up space)
  const expanded = dirty && shown;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: expanded ? "1fr" : "0fr",
        transition: "grid-template-columns 0.25s ease",
        overflow: "hidden",
      }}
      onTransitionEnd={() => {
        if (!dirty) setShown(false);
      }}
    >
      <div style={{ minWidth: 0 }}>
        <IconButton
          title={`Save scene (${getShortcutLabel("saveScene")})`}
          onClick={saveSceneToCloudLibrary}
          disabled={saving || !dirty}
          label="Save scene"
          icon={
            saving ? (
              <CubeSpinner size={18} variant="dark" />
            ) : (
              <span style={{ color: COLOR_CAUTION }}>
                <SaveIcon />
              </span>
            )
          }
          size={32}
          style={{
            border: "1.5px solid transparent",
            background: "transparent",
            pointerEvents: dirty ? "auto" : "none",
            flexShrink: 0,
            opacity: expanded ? 1 : 0,
            transform: expanded ? "scale(1)" : "scale(0.5)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// useSceneStats — scene statistics hook
// ---------------------------------------------------------------------------

/** Hook that computes scene statistics (object, face, vertex counts). */
export function useSceneStats() {
  const focalLength = useStore((s) => s.focalLength);
  const blendFiles = useStore((s) => s.blendFiles);
  const sceneObjects = useStore((s) => s.sceneObjects);

  const { objectCount, faceCount, vertCount } = useMemo(() => {
    let objects = 0;
    let faces = 0;
    let verts = 0;

    for (const entry of blendFiles.values()) {
      objects += entry.file.objects.length;
      for (const mesh of entry.file.meshes.values()) {
        verts += mesh.vertexCount;
        faces += mesh.polyFaceCount;
      }
    }

    for (const obj of sceneObjects.values()) {
      verts += obj.vertices.length / 3;
      faces += topologyFaceCount(obj.topology);
    }

    return {
      objectCount: objects + sceneObjects.size,
      faceCount: faces,
      vertCount: verts,
    };
  }, [blendFiles, sceneObjects]);

  return { focalLength, objectCount, faceCount, vertCount };
}

// ---------------------------------------------------------------------------
// CommandOverlay — floating command display (bottom-right)
// ---------------------------------------------------------------------------

function CommandLabel({ command }: { command: CommandBuffer }) {
  const meta = getCommandMetadata(command.type);
  const label = meta.label;
  const value = command.numericInput || null;

  // Build param display for parameterized ops (e.g., "0.15 | Segments: 3")
  let paramDisplay: string | null = null;
  if (!value) {
    const parts: string[] = [];
    for (const p of meta.params) {
      const v = command.params[p.key];
      if (v === undefined || v === p.default) continue;
      if (p.type === "float") {
        parts.push(`${p.label}: ${(v as number).toFixed(p.precision ?? 2)}`);
      } else if (p.type === "int") {
        parts.push(`${p.label}: ${v}`);
      }
    }
    if (parts.length > 0) paramDisplay = parts.join("  ");
  }

  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ color: COLOR_ACCENT, fontWeight: 600 }}>{label}</span>
      {value && (
        <>
          <span style={{ color: COLOR_TEXT_DISABLED }}>:</span>
          <span style={{ color: COLOR_TEXT }}>{value}</span>
        </>
      )}
      {paramDisplay && (
        <>
          <span style={{ color: COLOR_TEXT_DISABLED }}>:</span>
          <span style={{ color: COLOR_TEXT }}>{paramDisplay}</span>
        </>
      )}
      <span
        style={{
          color: COLOR_ACCENT,
          animation: "blink 1s step-end infinite",
        }}
      >
        |
      </span>
    </span>
  );
}

/**
 * Floating command display positioned in the bottom-right corner,
 * near the axis indicator gizmo — equidistant from the toolbar strip
 * and sidebar edge.  Text is right-aligned so long commands expand left.
 */
export function CommandOverlay() {
  ensureKeyframes();

  const commandBuffer = useStore((s) => s.commandBuffer);
  const chromeDim = useChromeDim();

  if (!commandBuffer) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "calc(44px + env(safe-area-inset-bottom, 0px) * 0.5 + 28px)",
        right: 8,
        maxWidth: "60%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "4px 10px",
        background: "rgba(14,14,16,0.8)",
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderRadius: RADIUS_SM,
        border: `0.5px solid ${COLOR_BORDER}`,
        zIndex: Z_TOOLBAR,
        pointerEvents: "none",
        fontSize: FONT_SIZE_TINY,
        whiteSpace: "nowrap",
        ...chromeDim,
      }}
    >
      <CommandLabel command={commandBuffer} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bottom-right badges
// ---------------------------------------------------------------------------

const BOTTOM_RIGHT_BADGE_OFFSET =
  "calc(44px + env(safe-area-inset-bottom, 0px) * 0.5 + 8px)";
const BADGE_BACKGROUND = "rgba(14,14,16,0.8)";

function OverlayBadge({
  children,
  onClick,
  color = COLOR_TEXT_DISABLED,
}: {
  children: ReactNode;
  onClick?: () => void;
  color?: string;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        minWidth: 0,
        maxWidth: "40vw",
        display: "flex",
        alignItems: "center",
        padding: "4px 10px",
        background: BADGE_BACKGROUND,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderRadius: RADIUS_SM,
        border: `0.5px solid ${COLOR_BORDER}`,
        fontSize: FONT_SIZE_TINY,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color,
        cursor: onClick ? "pointer" : "default",
        pointerEvents: onClick ? "auto" : "none",
      }}
    >
      {children}
    </div>
  );
}

function pluralize(
  count: number,
  singular: string,
  plural = `${singular}s`,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function useSelectionBadgeLabel(): string | null {
  return useStore((state) => {
    const editMode = state.editMode;
    if (state.workspaceMode === "edit" && editMode) {
      if (editMode.isCurve) {
        return editMode.selectedVertices.size > 0
          ? pluralize(editMode.selectedVertices.size, "point")
          : null;
      }
      switch (editMode.granularity) {
        case "face":
          return editMode.selectedFaces.size > 0
            ? pluralize(editMode.selectedFaces.size, "face")
            : null;
        case "edge":
          return editMode.selectedEdges.size > 0
            ? pluralize(editMode.selectedEdges.size, "edge")
            : null;
        default:
          return editMode.selectedVertices.size > 0
            ? pluralize(editMode.selectedVertices.size, "vertex", "vertices")
            : null;
      }
    }

    const selectedIds = [
      ...selectionToLeafObjectIds(
        state.blendFiles,
        state.collections,
        state.selectedObjectTargets,
      ),
    ];
    if (selectedIds.length === 0) return null;
    if (selectedIds.length > 1) return pluralize(selectedIds.length, "object");

    const selectedId = selectedIds[0];
    const sceneObject = state.sceneObjects.get(selectedId);
    if (sceneObject) return sceneObject.name;

    return selectedId;
  });
}

function SelectionBadge() {
  const label = useSelectionBadgeLabel();

  if (!label) return null;

  return <OverlayBadge>{label}</OverlayBadge>;
}

function SceneNameBadge() {
  const currentSceneFileName = useStore((s) => s.currentSceneFileName);
  const [renameOpen, setRenameOpen] = useState(false);

  const displayName = currentSceneFileName
    ? currentSceneFileName.replace(/\.pr$/i, "")
    : "Untitled Scene";

  return (
    <>
      <OverlayBadge onClick={() => setRenameOpen(true)}>
        {displayName}
      </OverlayBadge>
      <RenameSceneModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
      />
    </>
  );
}

export function BottomRightBadges() {
  const chromeDim = useChromeDim();
  return (
    <div
      style={{
        position: "absolute",
        right: 8,
        bottom: BOTTOM_RIGHT_BADGE_OFFSET,
        maxWidth: "70%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 8,
        zIndex: Z_TOOLBAR,
        pointerEvents: "none",
        ...chromeDim,
      }}
    >
      <SelectionBadge />
      <SceneNameBadge />
    </div>
  );
}

// ---------------------------------------------------------------------------
// RenameSceneModal — modal dialog for editing the scene name
// ---------------------------------------------------------------------------

function RenameSceneModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const currentSceneFileName = useStore((s) => s.currentSceneFileName);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayName = currentSceneFileName
    ? currentSceneFileName.replace(/\.pr$/i, "")
    : "";

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.value = displayName;
          inputRef.current.select();
        }
      });
    }
  }, [open, displayName]);

  const commit = useCallback(() => {
    const raw = inputRef.current?.value?.trim() ?? "";
    if (raw) {
      const name = raw.endsWith(".pr") ? raw : raw + ".pr";
      useStore.setState({ currentSceneFileName: name });
    }
    onClose();
  }, [onClose]);

  return (
    <ModalShell open={open} onClose={onClose} onConfirm={commit} width={340}>
      <span
        style={{
          fontSize: FONT_SIZE_MODAL_TITLE,
          fontWeight: 600,
          color: COLOR_TEXT,
        }}
      >
        Rename Scene
      </span>

      <input
        ref={inputRef}
        defaultValue={displayName}
        style={PANEL_INPUT_STYLE}
        onKeyDown={(e) => e.stopPropagation()}
      />

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onClose} variant="secondary">
          Cancel
        </Button>
        <Button onClick={commit} variant="primary">
          Rename
        </Button>
      </div>
    </ModalShell>
  );
}
